/**
 * Versioned transaction utilities for dust swapping
 *
 * Handles deserialization and signing of VersionedTransactions
 * with address lookup table support.
 */

import {
  Connection,
  VersionedTransaction,
  Transaction,
  AddressLookupTableAccount,
  PublicKey,
} from '@solana/web3.js';

/**
 * Resolve address lookup table accounts from their addresses
 */
export async function getAddressLookupTableAccounts(
  connection: Connection,
  addresses: string[]
): Promise<AddressLookupTableAccount[]> {
  if (addresses.length === 0) {
    return [];
  }

  const accountInfos = await connection.getMultipleAccountsInfo(
    addresses.map((addr) => new PublicKey(addr))
  );

  return accountInfos.reduce((acc, accountInfo, index) => {
    const address = addresses[index];
    if (accountInfo) {
      const lookupTableAccount = new AddressLookupTableAccount({
        key: new PublicKey(address),
        state: AddressLookupTableAccount.deserialize(accountInfo.data),
      });
      acc.push(lookupTableAccount);
    }
    return acc;
  }, new Array<AddressLookupTableAccount>());
}

/**
 * Deserialize a hex-encoded versioned transaction
 */
export function deserializeVersionedTransaction(serializedTx: string): VersionedTransaction {
  const txBuffer = Buffer.from(serializedTx, 'hex');
  return VersionedTransaction.deserialize(txBuffer);
}

// Confirmation timeout in milliseconds (60 seconds to handle network congestion)
const CONFIRMATION_TIMEOUT_MS = 60000;

/**
 * Confirm a transaction with timeout and fallback verification
 *
 * Uses blockhash strategy for reliable confirmation, with fallback to
 * getTransaction check if confirmation times out (transaction may have
 * succeeded but confirmation was slow).
 */
async function confirmTransactionWithRetry(
  connection: Connection,
  signature: string,
  blockhash: string,
  lastValidBlockHeight: number
): Promise<void> {
  console.log('[SWAP-TX] Confirming transaction with blockhash strategy...');

  try {
    // Race between confirmation and timeout
    await Promise.race([
      connection.confirmTransaction(
        {
          signature,
          blockhash,
          lastValidBlockHeight,
        },
        'confirmed'
      ),
      new Promise((_, reject) =>
        setTimeout(() => reject(new Error('CONFIRMATION_TIMEOUT')), CONFIRMATION_TIMEOUT_MS)
      ),
    ]);

    console.log(`[SWAP-TX] Transaction confirmed: ${signature}`);
  } catch (confirmError: any) {
    if (confirmError.message === 'CONFIRMATION_TIMEOUT') {
      // Timeout - check if transaction actually exists on-chain
      console.warn('[SWAP-TX] Confirmation timeout, verifying transaction status...');

      const txInfo = await connection.getTransaction(signature, {
        commitment: 'confirmed',
        maxSupportedTransactionVersion: 0,
      });

      if (txInfo) {
        // Transaction exists
        if (txInfo.meta?.err) {
          throw new Error(`Transaction failed on-chain: ${JSON.stringify(txInfo.meta.err)}`);
        }
        // Transaction succeeded but confirmation was slow
        console.log(`[SWAP-TX] Transaction verified as confirmed: ${signature}`);
        return;
      }

      // Transaction not found - might still be processing, try one more time
      console.log('[SWAP-TX] Transaction not found, waiting and retrying verification...');
      await new Promise((resolve) => setTimeout(resolve, 2000));

      const txInfoRetry = await connection.getTransaction(signature, {
        commitment: 'confirmed',
        maxSupportedTransactionVersion: 0,
      });

      if (txInfoRetry) {
        if (txInfoRetry.meta?.err) {
          throw new Error(`Transaction failed on-chain: ${JSON.stringify(txInfoRetry.meta.err)}`);
        }
        console.log(`[SWAP-TX] Transaction verified as confirmed (retry): ${signature}`);
        return;
      }

      // Still not found - this is a genuine timeout/failure
      throw new Error(
        'Transaction confirmation timeout. The transaction may still be processing - please check your wallet and refresh the page.'
      );
    }

    // Re-throw other confirmation errors
    throw confirmError;
  }
}

/**
 * Sign and send a versioned transaction
 *
 * @param serializedTx - Hex-encoded serialized transaction from backend
 * @param walletProvider - Wallet provider with sign capability
 * @param connection - Solana connection
 * @returns Transaction signature
 */
export async function signAndSendVersionedTransaction(
  serializedTx: string,
  walletProvider: any,
  connection: Connection
): Promise<string> {
  console.log('[SWAP-TX] Deserializing versioned transaction...');

  // Deserialize the transaction
  const transaction = deserializeVersionedTransaction(serializedTx);

  // Store original signatures before signing (for Gate Wallet signature restoration)
  const originalSignatures: Map<string, Buffer> = new Map();
  for (let i = 0; i < transaction.message.staticAccountKeys.length; i++) {
    const pubkey = transaction.message.staticAccountKeys[i];
    const signature = transaction.signatures[i];
    if (signature && !signature.every((b) => b === 0)) {
      originalSignatures.set(pubkey.toString(), Buffer.from(signature));
    }
  }

  console.log(`[SWAP-TX] Found ${originalSignatures.size} existing signatures`);

  // Get fresh blockhash for confirmation tracking
  // We need this because the backend's blockhash in the transaction may be old
  const { blockhash, lastValidBlockHeight } = await connection.getLatestBlockhash('confirmed');
  console.log(`[SWAP-TX] Got blockhash for confirmation tracking (valid until block ${lastValidBlockHeight})`);

  // Sign with wallet
  console.log('[SWAP-TX] Requesting user signature...');
  let signedTransaction: VersionedTransaction;

  if (typeof walletProvider.signTransaction === 'function') {
    signedTransaction = await walletProvider.signTransaction(transaction);
  } else {
    throw new Error('Wallet does not support transaction signing');
  }

  // Restore any signatures that may have been corrupted by wallet (Gate Wallet issue)
  for (const [pubkeyStr, originalSig] of originalSignatures) {
    const signerIndex = signedTransaction.message.staticAccountKeys.findIndex(
      (key) => key.toString() === pubkeyStr
    );

    if (signerIndex >= 0 && signerIndex < signedTransaction.signatures.length) {
      const currentSig = signedTransaction.signatures[signerIndex];
      const currentSigHex = Buffer.from(currentSig).toString('hex');
      const originalSigHex = originalSig.toString('hex');

      // Check if signature was corrupted (all zeros or different from original)
      const isCorrupted =
        currentSig.every((b) => b === 0) || currentSigHex !== originalSigHex;

      if (isCorrupted) {
        console.log(
          `[SWAP-TX] Restoring signature for ${pubkeyStr.slice(0, 8)}... (was ${currentSig.every((b) => b === 0) ? 'missing' : 'corrupted'})`
        );
        signedTransaction.signatures[signerIndex] = originalSig;
      }
    }
  }

  // Send the transaction
  console.log('[SWAP-TX] Sending transaction...');
  const signature = await connection.sendRawTransaction(signedTransaction.serialize(), {
    skipPreflight: false,
    preflightCommitment: 'confirmed',
    maxRetries: 3,
  });

  console.log(`[SWAP-TX] Transaction sent: ${signature}`);

  // Confirm the transaction with robust timeout and fallback verification
  await confirmTransactionWithRetry(connection, signature, blockhash, lastValidBlockHeight);

  return signature;
}

/**
 * Batch sign and send multiple versioned transactions
 *
 * Signs all transactions at once for better UX (single wallet popup),
 * then sends them sequentially.
 *
 * @param serializedTxs - Array of hex-encoded serialized transactions
 * @param walletProvider - Wallet provider with sign capability
 * @param connection - Solana connection
 * @returns Array of transaction signatures
 */
export async function signAndSendMultipleVersionedTransactions(
  serializedTxs: string[],
  walletProvider: any,
  connection: Connection
): Promise<string[]> {
  if (serializedTxs.length === 0) {
    return [];
  }

  console.log(`[SWAP-TX] Processing ${serializedTxs.length} transactions...`);

  // Deserialize all transactions
  const transactions = serializedTxs.map((tx) => deserializeVersionedTransaction(tx));

  // Store original signatures for all transactions
  const originalSignaturesPerTx: Map<string, Buffer>[] = transactions.map((tx) => {
    const sigs = new Map<string, Buffer>();
    for (let i = 0; i < tx.message.staticAccountKeys.length; i++) {
      const pubkey = tx.message.staticAccountKeys[i];
      const signature = tx.signatures[i];
      if (signature && !signature.every((b) => b === 0)) {
        sigs.set(pubkey.toString(), Buffer.from(signature));
      }
    }
    return sigs;
  });

  // Sign all transactions at once
  console.log('[SWAP-TX] Requesting user to sign all transactions...');
  let signedTransactions: VersionedTransaction[];

  if (typeof walletProvider.signAllTransactions === 'function') {
    signedTransactions = await walletProvider.signAllTransactions(transactions);
  } else if (typeof walletProvider.signTransaction === 'function') {
    // Fallback: sign one by one
    console.warn('[SWAP-TX] signAllTransactions not available, falling back to individual signing');
    signedTransactions = [];
    for (const tx of transactions) {
      const signed = await walletProvider.signTransaction(tx);
      signedTransactions.push(signed);
    }
  } else {
    throw new Error('Wallet does not support transaction signing');
  }

  // Restore any corrupted signatures
  for (let i = 0; i < signedTransactions.length; i++) {
    const tx = signedTransactions[i];
    const originalSigs = originalSignaturesPerTx[i];

    for (const [pubkeyStr, originalSig] of originalSigs) {
      const signerIndex = tx.message.staticAccountKeys.findIndex(
        (key) => key.toString() === pubkeyStr
      );

      if (signerIndex >= 0 && signerIndex < tx.signatures.length) {
        const currentSig = tx.signatures[signerIndex];
        const currentSigHex = Buffer.from(currentSig).toString('hex');
        const originalSigHex = originalSig.toString('hex');

        const isCorrupted =
          currentSig.every((b) => b === 0) || currentSigHex !== originalSigHex;

        if (isCorrupted) {
          console.log(
            `[SWAP-TX:${i + 1}] Restoring signature for ${pubkeyStr.slice(0, 8)}...`
          );
          tx.signatures[signerIndex] = originalSig;
        }
      }
    }
  }

  // Send transactions sequentially
  const signatures: string[] = [];

  for (let i = 0; i < signedTransactions.length; i++) {
    const tx = signedTransactions[i];
    console.log(`[SWAP-TX] Sending transaction ${i + 1}/${signedTransactions.length}...`);

    try {
      // Get fresh blockhash for each transaction confirmation
      const { blockhash, lastValidBlockHeight } = await connection.getLatestBlockhash('confirmed');

      const signature = await connection.sendRawTransaction(tx.serialize(), {
        skipPreflight: false,
        preflightCommitment: 'confirmed',
        maxRetries: 3,
      });

      console.log(`[SWAP-TX:${i + 1}] Transaction sent: ${signature}`);

      // Confirm with robust timeout and fallback verification
      await confirmTransactionWithRetry(connection, signature, blockhash, lastValidBlockHeight);

      console.log(`[SWAP-TX:${i + 1}] Transaction confirmed`);
      signatures.push(signature);
    } catch (error: any) {
      // Check if already processed
      const errorMessage = error?.message || String(error);
      const isAlreadyProcessed =
        errorMessage.includes('already been processed') ||
        errorMessage.includes('already processed');

      if (isAlreadyProcessed) {
        console.log(`[SWAP-TX:${i + 1}] Transaction was already processed, verifying...`);

        // Extract signature from the signed transaction and verify it
        const txSignatureBytes = tx.signatures[0];
        if (txSignatureBytes && !txSignatureBytes.every((b) => b === 0)) {
          // Convert to base58 signature
          const bs58 = await import('bs58');
          const signatureBase58 = bs58.default.encode(txSignatureBytes);

          // Verify the transaction status
          const txInfo = await connection.getTransaction(signatureBase58, {
            commitment: 'confirmed',
            maxSupportedTransactionVersion: 0,
          });

          if (txInfo && !txInfo.meta?.err) {
            console.log(`[SWAP-TX:${i + 1}] Verified already processed transaction: ${signatureBase58}`);
            signatures.push(signatureBase58);
            continue;
          }
        }
      }

      console.error(`[SWAP-TX:${i + 1}] Transaction failed:`, error);
      throw error;
    }
  }

  return signatures;
}

/**
 * Deserialize a hex-encoded legacy transaction
 */
export function deserializeLegacyTransaction(serializedTx: string): Transaction {
  const txBuffer = Buffer.from(serializedTx, 'hex');
  return Transaction.from(txBuffer);
}

/**
 * Sign and send a legacy transaction (used for wrapUp transactions)
 *
 * @param serializedTx - Hex-encoded serialized transaction from backend
 * @param walletProvider - Wallet provider with sign capability
 * @param connection - Solana connection
 * @returns Transaction signature
 */
export async function signAndSendLegacyTransaction(
  serializedTx: string,
  walletProvider: any,
  connection: Connection
): Promise<string> {
  console.log('[WRAPUP-TX] Deserializing legacy transaction...');

  // Deserialize the transaction
  const transaction = deserializeLegacyTransaction(serializedTx);

  // Store original signatures before signing (for signature restoration)
  const originalSignatures: Map<string, Buffer> = new Map();
  if (transaction.signatures) {
    for (const signerPubkey of transaction.signatures) {
      if (signerPubkey.signature && signerPubkey.signature.length > 0) {
        originalSignatures.set(signerPubkey.publicKey.toString(), Buffer.from(signerPubkey.signature));
      }
    }
  }

  console.log(`[WRAPUP-TX] Found ${originalSignatures.size} existing signatures`);

  // Get fresh blockhash for confirmation tracking
  const { blockhash, lastValidBlockHeight } = await connection.getLatestBlockhash('confirmed');
  console.log(`[WRAPUP-TX] Got blockhash for confirmation tracking (valid until block ${lastValidBlockHeight})`);

  // Sign with wallet
  console.log('[WRAPUP-TX] Requesting user signature...');
  let signedTransaction: Transaction;

  if (typeof walletProvider.signTransaction === 'function') {
    signedTransaction = await walletProvider.signTransaction(transaction);
  } else {
    throw new Error('Wallet does not support transaction signing');
  }

  // Restore any signatures that may have been corrupted by wallet
  for (const [pubkeyStr, originalSig] of originalSignatures) {
    const signerEntry = signedTransaction.signatures.find(
      (s) => s.publicKey.toString() === pubkeyStr
    );

    if (signerEntry && signerEntry.signature) {
      const currentSigHex = Buffer.from(signerEntry.signature).toString('hex');
      const originalSigHex = originalSig.toString('hex');

      const isCorrupted =
        signerEntry.signature.every((b) => b === 0) || currentSigHex !== originalSigHex;

      if (isCorrupted) {
        console.log(
          `[WRAPUP-TX] Restoring signature for ${pubkeyStr.slice(0, 8)}...`
        );
        signerEntry.signature = originalSig;
      }
    }
  }

  // Send the transaction
  console.log('[WRAPUP-TX] Sending transaction...');
  const signature = await connection.sendRawTransaction(signedTransaction.serialize(), {
    skipPreflight: false,
    preflightCommitment: 'confirmed',
    maxRetries: 3,
  });

  console.log(`[WRAPUP-TX] Transaction sent: ${signature}`);

  // Confirm the transaction with robust timeout and fallback verification
  await confirmTransactionWithRetry(connection, signature, blockhash, lastValidBlockHeight);

  return signature;
}
